import React, { useEffect, useState, useCallback } from 'react';
import { Form, Row, Col, Alert, Modal, Button, Card, ProgressBar, Badge } from 'react-bootstrap';
import { 
  getStudentsBySchoolId,
  getClassesBySchoolId,
  getIndividualStudentResults,
  getAllAnswersByAssessmentInstance,
  getDeletedAnswersByAssessmentInstance
} from "../services/APIClient";

const RetakeTaskComparisonModal = ({ show, onHide, schoolid }) => {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Selection state
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [selectedScreeningPoint, setSelectedScreeningPoint] = useState('');
  const [selectedTask, setSelectedTask] = useState('');
  const [comparisonData, setComparisonData] = useState(null);
  
  // State for available options
  const [availableScreeningPoints, setAvailableScreeningPoints] = useState([]);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [loadingScreeningPoints, setLoadingScreeningPoints] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);

  // New state for confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'original' or 'retake'
  const [isProcessingDefault, setIsProcessingDefault] = useState(false);

  // Helper function to get grade label from class
  const getGradeLabelFromClass = (classItem) => {
    if (!classItem) return "Unknown Grade";
    if (classItem.grade_level1) return "Junior Infants";
    if (classItem.grade_level2) return "Senior Infants";
    if (classItem.grade_level3) return "1st Class";
    if (classItem.grade_level4) return "2nd Class";
    if (classItem.grade_level5) return "3rd Class";
    if (classItem.grade_level6) return "4th Class";
    if (classItem.grade_level7) return "5th Class";
    if (classItem.grade_level8) return "6th Class";
    return "Unknown Grade";
  };

  const loadClasses = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('📚 DEBUG: Loading classes for school:', schoolid);
      const classesResponse = await getClassesBySchoolId(schoolid);
      
      const formattedClasses = (classesResponse || []).map(classItem => {
        const gradeLabel = getGradeLabelFromClass(classItem);
        
        return {
          id: classItem.id,
          name: classItem.name,
          grade: gradeLabel,
          displayName: `${classItem.name} (${gradeLabel})`
        };
      }).sort((a, b) => {
        // Sort by grade level first, then by name
        const gradeOrder = {
          "Junior Infants": 1,
          "Senior Infants": 2,
          "1st Class": 3,
          "2nd Class": 4,
          "3rd Class": 5,
          "4th Class": 6,
          "5th Class": 7,
          "6th Class": 8,
          "Unknown Grade": 9
        };
        
        const gradeA = gradeOrder[a.grade] || 9;
        const gradeB = gradeOrder[b.grade] || 9;
        
        if (gradeA !== gradeB) {
          return gradeA - gradeB;
        }
        
        return a.name.localeCompare(b.name);
      });

      console.log('📚 DEBUG: Formatted classes:', formattedClasses);
      setClasses(formattedClasses);
    } catch (err) {
      console.error('Error loading classes:', err);
      setError('Failed to load classes. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [schoolid]);

  const loadStudentsForClass = useCallback(async (classId) => {
    if (!classId) {
      setStudents([]);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      console.log('👥 DEBUG: Loading students for class:', classId);
      // Get all students for the school then filter by class
      const studentsResponse = await getStudentsBySchoolId(schoolid);
      
      // Filter students by selected class
      const classStudents = (studentsResponse || []).filter(student => 
        student.classId && student.classId.toString() === classId.toString()
      );
      
      const formattedStudents = classStudents.map(student => {
        return {
          id: student.id,
          name: student.name,
          firstName: student.firstName,
          lastName: student.lastName,
          classId: student.classId,
          className: student.class?.name || 'Unknown Class',
          grade: student.class ? getGradeLabelFromClass(student.class) : 'Unknown Grade'
        };
      }).sort((a, b) => {
        // Sort alphabetically by display name
        const nameA = (a.firstName && a.lastName) ? `${a.firstName} ${a.lastName}` : a.name || '';
        const nameB = (b.firstName && b.lastName) ? `${b.firstName} ${b.lastName}` : b.name || '';
        return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
      });

      console.log('👥 DEBUG: Formatted students for class:', formattedStudents);
      setStudents(formattedStudents);
    } catch (err) {
      console.error('Error loading students for class:', err);
      setError('Failed to load students for this class. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [schoolid]);

  const getAvailableScreeningPointsForStudent = async (studentId) => {
    console.log('🔍 DEBUG START: Getting screening points for student:', studentId);
    
    if (!studentId) {
      console.log('❌ DEBUG: Missing student ID - returning empty array');
      return [];
    }

    try {
      console.log('📡 DEBUG: Fetching student assessment results...');
      
      // Get all assessments for the student
      const studentAssessments = await getIndividualStudentResults(studentId);
      console.log('📡 DEBUG: Student assessments response:', studentAssessments);

      if (!studentAssessments || studentAssessments.length === 0) {
        console.log('❌ DEBUG: No assessments found for student');
        return [];
      }

      // Filter out RTI assessments - only include standard screeners
      const standardScreeners = studentAssessments.filter(assessment => {
        if (assessment.rti === true) {
          console.log('🚫 DEBUG: Excluding RTI assessment:', assessment.name);
          return false;
        }
        return true;
      });

      console.log('📋 DEBUG: Standard screeners after RTI filter:', standardScreeners);

      // Return all standard assessment names
      const finalOptions = standardScreeners.map(assessment => assessment.name);
      
      console.log('✅ DEBUG: Final screening point options returned:', finalOptions);
      console.log('🔍 DEBUG END: Completed processing for student:', studentId);
      
      return finalOptions;

    } catch (error) {
      console.error('💥 DEBUG: Error in getAvailableScreeningPointsForStudent:', error);
      return [];
    }
  };

  const getAvailableTasksForScreeningPoint = async (studentId, screeningPointName) => {
    console.log('🎯 DEBUG START: Getting tasks for screening point:', { studentId, screeningPointName });
    
    if (!studentId || !screeningPointName) {
      console.log('❌ DEBUG: Missing parameters - returning empty array');
      return [];
    }

    try {
      // Get the student's assessments to find the matching assessment instance
      const studentAssessments = await getIndividualStudentResults(studentId);
      
      // Filter to standard screeners only (exclude RTI)
      const standardScreeners = studentAssessments.filter(assessment => assessment.rti !== true);
      
      const matchingAssessment = standardScreeners.find(assessment => 
        assessment.name === screeningPointName
      );

      if (!matchingAssessment) {
        console.log('❌ DEBUG: No matching assessment found');
        return [];
      }

      const assessmentInstanceId = matchingAssessment.assessmentInstanceId || matchingAssessment.id;
      console.log('🎯 DEBUG: Found matching assessment instance:', assessmentInstanceId);

      // Get all answers for this assessment instance
      const allAnswers = await getAllAnswersByAssessmentInstance(assessmentInstanceId);
      console.log('🎯 DEBUG: All answers retrieved:', allAnswers);

      const { activeAnswers, deletedAnswers } = allAnswers;

      // Get unique task names from both active and deleted answers
      const taskNames = new Set();
      
      if (activeAnswers && activeAnswers.length > 0) {
        activeAnswers.forEach(answer => {
          if (answer.taskName) {
            taskNames.add(answer.taskName);
          }
        });
      }
      
      if (deletedAnswers && deletedAnswers.length > 0) {
        deletedAnswers.forEach(answer => {
          if (answer.taskName) {
            taskNames.add(answer.taskName);
          }
        });
      }

      const availableTasks = Array.from(taskNames).sort();
      console.log('✅ DEBUG: Available tasks found:', availableTasks);
      
      return availableTasks;

    } catch (error) {
      console.error('💥 DEBUG: Error in getAvailableTasksForScreeningPoint:', error);
      return [];
    }
  };

  const getTaskComparisonData = async (studentId, screeningPointName, taskName) => {
    if (!studentId || !screeningPointName || !taskName) {
      return null;
    }

    try {
      console.log('📊 DEBUG: getTaskComparisonData called with:', {
        studentId,
        screeningPointName,
        taskName
      });

      // First get the student's assessments to find the matching assessment instance
      const studentAssessments = await getIndividualStudentResults(studentId);
      
      // Filter to standard screeners only (exclude RTI)
      const standardScreeners = studentAssessments.filter(assessment => assessment.rti !== true);
      
      const matchingAssessment = standardScreeners.find(assessment => 
        assessment.name === screeningPointName
      );

      if (!matchingAssessment) {
        return {
          error: `Assessment "${screeningPointName}" not found for this student.`
        };
      }

      const assessmentInstanceId = matchingAssessment.assessmentInstanceId || matchingAssessment.id;
      console.log('📊 DEBUG: Found matching assessment instance:', assessmentInstanceId);

      // Get all answers for this assessment instance
      const allAnswers = await getAllAnswersByAssessmentInstance(assessmentInstanceId);
      console.log('📊 DEBUG: All answers retrieved:', allAnswers);

      const { activeAnswers, deletedAnswers } = allAnswers;

      // Filter answers for the specific task
      const activeTaskAnswers = activeAnswers?.filter(answer => answer.taskName === taskName) || [];
      const deletedTaskAnswers = deletedAnswers?.filter(answer => answer.taskName === taskName) || [];

      console.log('📊 DEBUG: Task-specific answers:', {
        activeTaskAnswers: activeTaskAnswers.length,
        deletedTaskAnswers: deletedTaskAnswers.length
      });

      // Handle case where there are no active answers for this task
      if (activeTaskAnswers.length === 0) {
        return {
          error: `No current answers found for task "${taskName}".`
        };
      }

      // Get the active (current) task data
      const activeTask = activeTaskAnswers[0]; // Should be only one answer per task
      const activeDate = activeTask?.submitted;

      const rightResult = {
        taskName: activeTask.taskName || 'Unknown Task',
        score: Math.round((activeTask.score || 0) * 100),
        maxScore: 100,
        timeSpent: activeTask.duration || 0,
        taskOrder: activeTask.taskOrder || 0
      };

      // Handle case where there are no deleted answers for this task (no retakes)
      if (deletedTaskAnswers.length === 0) {
        return {
          left: {
            date: 'Original Attempt (No Data)',
            results: [],
            noData: true,
            isOriginal: true
          },
          right: {
            date: activeDate ? `Current Attempt (${new Date(activeDate).toLocaleDateString()})` : 'Current Attempt',
            results: [rightResult],
            isRetake: false // Not actually a retake since no original was deleted
          },
          assessmentInstanceId: assessmentInstanceId,
          taskName: taskName
        };
      }

      // Get the deleted (original) task data
      const deletedTask = deletedTaskAnswers[0]; // Should be only one answer per task
      const deletedDate = deletedTask?.dateDeleted || deletedTask?.submitted;

      const leftResult = {
        taskName: deletedTask.taskName || 'Unknown Task',
        score: Math.round((deletedTask.score || 0) * 100),
        maxScore: 100,
        timeSpent: deletedTask.duration || 0,
        taskOrder: deletedTask.taskOrder || 0
      };

      const result = {
        left: {
          date: deletedDate ? `Original Attempt (${new Date(deletedDate).toLocaleDateString()})` : 'Original Attempt',
          results: [leftResult],
          isOriginal: true
        },
        right: {
          date: activeDate ? `Retake Attempt (${new Date(activeDate).toLocaleDateString()})` : 'Retake Attempt',
          results: [rightResult],
          isRetake: true
        },
        assessmentInstanceId: assessmentInstanceId,
        taskName: taskName
      };

      console.log('📊 DEBUG: Final task comparison result:', result);
      return result;

    } catch (error) {
      console.error('Error fetching task comparison data:', error);
      return {
        error: `Failed to load task comparison data: ${error.message || 'Unknown error'}`
      };
    }
  };

  // New function to handle setting default results for a specific task
  const handleSetDefault = (attemptType) => {
    setConfirmAction(attemptType);
    setShowConfirmModal(true);
  };

  // New function to confirm setting default for a specific task
  const confirmSetDefault = async () => {
    if (!confirmAction || !comparisonData || !selectedStudent || !selectedScreeningPoint || !selectedTask) {
      return;
    }

    setIsProcessingDefault(true);
    
    try {
      // Here you would call your API to set the default results for the specific task
      // This is a placeholder - you'll need to implement the actual API call
      console.log('Setting default task results:', {
        studentId: selectedStudent,
        screeningPointName: selectedScreeningPoint,
        taskName: selectedTask,
        attemptType: confirmAction,
        assessmentInstanceId: comparisonData.assessmentInstanceId
      });

      // Example API call (you'll need to implement this):
      // await setDefaultTaskResults(
      //   comparisonData.assessmentInstanceId, 
      //   selectedTask,
      //   confirmAction === 'original' ? 'deleted' : 'active'
      // );

      // Show success message
      alert(`Successfully set ${confirmAction === 'original' ? 'original' : 'retake'} attempt as default for task "${selectedTask}"`);
      
      // Optionally refresh the data
      const refreshedData = await getTaskComparisonData(selectedStudent, selectedScreeningPoint, selectedTask);
      setComparisonData(refreshedData);

    } catch (error) {
      console.error('Error setting default task results:', error);
      alert('Failed to set default task results. Please try again.');
    } finally {
      setIsProcessingDefault(false);
      setShowConfirmModal(false);
      setConfirmAction(null);
    }
  };

  const loadAvailableScreeningPoints = useCallback(async (studentId) => {
    console.log('🚀 DEBUG: loadAvailableScreeningPoints called with:', {
      studentId,
      studentIdType: typeof studentId
    });
    
    setLoadingScreeningPoints(true);
    try {
      console.log('🚀 DEBUG: About to call getAvailableScreeningPointsForStudent...');
      const options = await getAvailableScreeningPointsForStudent(studentId);
      console.log('🚀 DEBUG: getAvailableScreeningPointsForStudent returned:', options);
      setAvailableScreeningPoints(options);
      console.log('🚀 DEBUG: Set availableScreeningPoints state to:', options);
    } catch (error) {
      console.error('💥 DEBUG: Error in loadAvailableScreeningPoints:', error);
      setAvailableScreeningPoints([]);
    } finally {
      setLoadingScreeningPoints(false);
      console.log('🚀 DEBUG: loadAvailableScreeningPoints completed');
    }
  }, []);

  const loadAvailableTasks = useCallback(async (studentId, screeningPointName) => {
    console.log('🎯 DEBUG: loadAvailableTasks called with:', {
      studentId,
      screeningPointName
    });
    
    setLoadingTasks(true);
    try {
      const tasks = await getAvailableTasksForScreeningPoint(studentId, screeningPointName);
      console.log('🎯 DEBUG: getAvailableTasksForScreeningPoint returned:', tasks);
      setAvailableTasks(tasks);
    } catch (error) {
      console.error('💥 DEBUG: Error in loadAvailableTasks:', error);
      setAvailableTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  }, []);

  // Event handlers
  const handleClassChange = (classId) => {
    console.log('🏫 DEBUG: handleClassChange called with:', classId);
    setSelectedClass(classId);
    setSelectedStudent('');
    setSelectedScreeningPoint('');
    setSelectedTask('');
    setComparisonData(null);
    setAvailableScreeningPoints([]);
    setAvailableTasks([]);
    
    if (classId) {
      loadStudentsForClass(classId);
    } else {
      setStudents([]);
    }
  };

  const handleStudentChange = (studentId) => {
    console.log('👤 DEBUG: handleStudentChange called with:', studentId);
    setSelectedStudent(studentId);
    setSelectedScreeningPoint('');
    setSelectedTask('');
    setComparisonData(null);
    setAvailableTasks([]);
  };

  const handleScreeningPointChange = (screeningPoint) => {
    console.log('📝 DEBUG: handleScreeningPointChange called with:', screeningPoint);
    setSelectedScreeningPoint(screeningPoint);
    setSelectedTask('');
    setComparisonData(null);
  };

  const handleTaskChange = async (taskName) => {
    console.log('🎯 DEBUG: handleTaskChange called with:', taskName);
    setSelectedTask(taskName);
    if (selectedStudent && selectedScreeningPoint && taskName) {
      console.log('🎯 DEBUG: Getting task comparison data...');
      const data = await getTaskComparisonData(selectedStudent, selectedScreeningPoint, taskName);
      console.log('🎯 DEBUG: Received task comparison data:', data);
      setComparisonData(data);
    }
  };

  // Load classes when modal opens
  useEffect(() => {
    if (show && schoolid) {
      loadClasses();
    }
  }, [show, schoolid, loadClasses]);

  // Load available screening points when student changes
  useEffect(() => {
    console.log('🔄 DEBUG: useEffect triggered for loading screening points:', {
      selectedStudent,
      shouldLoad: selectedStudent
    });
    
    if (selectedStudent) {
      console.log('✅ DEBUG: Student selected, calling loadAvailableScreeningPoints...');
      loadAvailableScreeningPoints(selectedStudent);
    } else {
      console.log('❌ DEBUG: No student selected, clearing screening points');
      setAvailableScreeningPoints([]);
    }
  }, [selectedStudent, loadAvailableScreeningPoints]);

  // Load available tasks when screening point changes
  useEffect(() => {
    console.log('🔄 DEBUG: useEffect triggered for loading tasks:', {
      selectedStudent,
      selectedScreeningPoint,
      shouldLoad: selectedStudent && selectedScreeningPoint
    });
    
    if (selectedStudent && selectedScreeningPoint) {
      console.log('✅ DEBUG: Student and screening point selected, calling loadAvailableTasks...');
      loadAvailableTasks(selectedStudent, selectedScreeningPoint);
    } else {
      console.log('❌ DEBUG: Prerequisites not met, clearing tasks');
      setAvailableTasks([]);
    }
  }, [selectedStudent, selectedScreeningPoint, loadAvailableTasks]);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setSelectedClass('');
      setSelectedStudent('');
      setSelectedScreeningPoint('');
      setSelectedTask('');
      setComparisonData(null);
      setAvailableScreeningPoints([]);
      setAvailableTasks([]);
      setClasses([]);
      setStudents([]);
      setError(null);
      setShowConfirmModal(false);
      setConfirmAction(null);
    }
  }, [show]);

  // Render task result card
  const renderTaskResult = (task, side) => (
    <Card key={`${task.taskName}-${side}`} className="mb-2">
      <Card.Body className="py-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>{task.taskName}</strong>
          <Badge variant={task.score >= 70 ? "success" : task.score >= 50 ? "warning" : "danger"}>
            {task.score}%
          </Badge>
        </div>
        <ProgressBar 
          now={task.score} 
          variant={task.score >= 70 ? "success" : task.score >= 50 ? "warning" : "danger"}
          className="mb-1"
        />
        <small className="text-muted">
          Time: {task.timeSpent}s
        </small>
      </Card.Body>
    </Card>
  );

  // Custom header content for explanation
  const renderCustomHeader = () => {
    return (
      <div className="mb-4">
        <Alert variant="info">
          <div className="d-flex align-items-center">
            <div className="me-3" style={{ fontSize: '1.5rem' }}>🎯</div>
            <div>
              <strong>Retake Task Comparison Tool</strong>
              <p className="mb-0 mt-1">
                Compare individual task performance between original attempts and retakes. 
                Select a specific task within a screening point to see detailed before/after comparison.
                Use the ✓ buttons to set which attempt should be the student's default result for this task.
              </p>
            </div>
          </div>
        </Alert>
        
        <Row>
          <Col md={6}>
            <div className="p-3 border rounded bg-light">
              <div className="d-flex align-items-center mb-2">
                <span className="me-2" style={{ fontSize: '1.2rem' }}>📝</span>
                <strong className="text-primary">Original Task Attempt</strong>
              </div>
              <small className="text-muted">
                Shows the deleted answers from the student's first attempt at this specific task
              </small>
            </div>
          </Col>
          <Col md={6}>
            <div className="p-3 border rounded bg-light">
              <div className="d-flex align-items-center mb-2">
                <span className="me-2" style={{ fontSize: '1.2rem' }}>🎯</span>
                <strong className="text-success">Retake Task Attempt</strong>
              </div>
              <small className="text-muted">
                Shows the current active answers from the student's retake attempt at this task
              </small>
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  const selectedStudentName = students?.find(s => s.id.toString() === selectedStudent)?.name || '';
  const selectedClassName = classes?.find(c => c.id.toString() === selectedClass)?.displayName || '';

  const getStudentDisplayName = () => {
    const student = students?.find(s => s.id.toString() === selectedStudent);
    if (!student) return '';
    return student.firstName && student.lastName 
      ? `${student.firstName} ${student.lastName}` 
      : student.name;
  };

  // Show loading state
  if (loading) {
    return (
      <Modal show={show} onHide={onHide} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Retake Task Comparison</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderCustomHeader()}
          <div className="text-center py-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading...</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // Show error state
  if (error) {
    return (
      <Modal show={show} onHide={onHide} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Retake Task Comparison</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderCustomHeader()}
          <div className="alert alert-danger">
            <h5>Error</h5>
            <p>{error}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl">
        <style jsx>{`
          .set-default-btn {
            padding: 4px 8px;
            font-size: 0.8rem;
            border-radius: 3px;
            border: 1px solid rgba(255,255,255,0.3);
            background: rgba(255,255,255,0.1);
            color: white;
            transition: background-color 0.2s;
          }

          .set-default-btn:hover {
            background: rgba(255,255,255,0.2);
            color: white;
          }

          .set-default-btn:focus {
            box-shadow: 0 0 0 2px rgba(255,255,255,0.5);
          }
        `}</style>
        <Modal.Header closeButton>
          <Modal.Title>Retake Task Comparison</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderCustomHeader()}
          
          <Row className="mb-4">
            <Col md={3}>
              <Form.Group>
                <Form.Label><strong>Select Class:</strong></Form.Label>
                <Form.Control 
                  as="select" 
                  value={selectedClass} 
                  onChange={(e) => handleClassChange(e.target.value)}
                >
                  <option value="">Choose a class...</option>
                  {classes?.map(classItem => (
                    <option key={classItem.id} value={classItem.id}>
                      {classItem.displayName}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label><strong>Select Student:</strong></Form.Label>
                <Form.Control 
                  as="select" 
                  value={selectedStudent} 
                  onChange={(e) => handleStudentChange(e.target.value)}
                  disabled={!selectedClass}
                >
                  <option value="">
                    {!selectedClass ? 'Select a class first...' : 'Choose a student...'}
                  </option>
                  {students?.map(student => {
                    const displayName = student.firstName && student.lastName 
                      ? `${student.firstName} ${student.lastName}` 
                      : student.name;
                    return (
                      <option key={student.id} value={student.id}>
                        {displayName}
                      </option>
                    );
                  })}
                </Form.Control>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label><strong>Select Screening Point:</strong></Form.Label>
                <Form.Control 
                  as="select" 
                  value={selectedScreeningPoint} 
                  onChange={(e) => handleScreeningPointChange(e.target.value)}
                  disabled={!selectedStudent || loadingScreeningPoints}
                >
                  <option value="">
                    {loadingScreeningPoints ? 'Loading...' : 'Choose screening point...'}
                  </option>
                  {availableScreeningPoints.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Control>
                {loadingScreeningPoints && (
                  <small className="text-muted">
                    <div className="spinner-border spinner-border-sm me-1" role="status"></div>
                    Loading screening points...
                  </small>
                )}
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label><strong>Select Task:</strong></Form.Label>
                <Form.Control 
                  as="select" 
                  value={selectedTask} 
                  onChange={(e) => handleTaskChange(e.target.value)}
                  disabled={!selectedStudent || !selectedScreeningPoint || loadingTasks}
                >
                  <option value="">
                    {loadingTasks ? 'Loading tasks...' : 'Choose a task...'}
                  </option>
                  {availableTasks.map(task => (
                    <option key={task} value={task}>
                      {task}
                    </option>
                  ))}
                </Form.Control>
                {loadingTasks && (
                  <small className="text-muted">
                    <div className="spinner-border spinner-border-sm me-1" role="status"></div>
                    Loading tasks...
                  </small>
                )}
              </Form.Group>
            </Col>
          </Row>

          {selectedStudentName && (
            <Row className="mb-3">
              <Col md={12}>
                <div className="alert alert-light border">
                  <strong>Comparing:</strong> {selectedStudentName} 
                  {selectedClassName && <><span className="text-muted"> from {selectedClassName}</span></>}
                  {selectedScreeningPoint && <><br/><strong>Screening:</strong> {selectedScreeningPoint}</>}
                  {selectedTask && <><span> • <strong>Task:</strong> {selectedTask}</span></>}
                </div>
              </Col>
            </Row>
          )}

          {comparisonData?.error && (
            <div className="alert alert-warning">
              <strong>No Data Available:</strong> {comparisonData.error}
            </div>
          )}

          {comparisonData && !comparisonData.error && (
            <Row>
              <Col md={6}>
                <Card>
                  <Card.Header className="bg-primary text-white">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="mb-0">📝 Original Task Attempt</h5>
                        {comparisonData.left.date && <small>📅 {comparisonData.left.date}</small>}
                        {comparisonData.left.isOriginal && (
                          <div className="mt-1">
                            <small className="badge bg-light text-dark">📚 Deleted Answer</small>
                          </div>
                        )}
                      </div>
                      {!comparisonData.left.noData && (
                        <Button 
                          className="set-default-btn"
                          size="sm"
                          onClick={() => handleSetDefault('original')}
                          title="Set original task attempt as default"
                        >
                          ✓ Set Default
                        </Button>
                      )}
                    </div>
                  </Card.Header>
                  <Card.Body>
                    {comparisonData.left.noData ? (
                      <div className="alert alert-info mb-0">
                        <strong>No original data available</strong> for this task
                      </div>
                    ) : comparisonData.left.results && comparisonData.left.results.length > 0 ? (
                      <>
                        {comparisonData.left.results.map(task => renderTaskResult(task, 'left'))}
                        <div className="mt-2 pt-2 border-top">
                          <small className="text-muted">
                            {comparisonData.left.isOriginal && <span className="text-primary">📚 From original attempt</span>}
                          </small>
                        </div>
                      </>
                    ) : (
                      <p className="text-muted mb-0">No task result available</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card>
                  <Card.Header className="bg-success text-white">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="mb-0">🎯 Retake Task Attempt</h5>
                        {comparisonData.right.date && <small>📅 {comparisonData.right.date}</small>}
                        {comparisonData.right.isRetake && (
                          <div className="mt-1">
                            <small className="badge bg-light text-dark">🎯 Active Answer</small>
                          </div>
                        )}
                      </div>
                      {!comparisonData.right.noData && (
                        <Button 
                          className="set-default-btn"
                          size="sm"
                          onClick={() => handleSetDefault('retake')}
                          title="Set retake task attempt as default"
                        >
                          ✓ Set Default
                        </Button>
                      )}
                    </div>
                  </Card.Header>
                  <Card.Body>
                    {comparisonData.right.noData ? (
                      <div className="alert alert-info mb-0">
                        <strong>No retake data available</strong> for this task
                      </div>
                    ) : comparisonData.right.results && comparisonData.right.results.length > 0 ? (
                      <>
                        {comparisonData.right.results.map(task => renderTaskResult(task, 'right'))}
                        <div className="mt-2 pt-2 border-top">
                          <small className="text-muted">
                            {comparisonData.right.isRetake && <span className="text-success">🎯 From retake attempt</span>}
                          </small>
                        </div>
                      </>
                    ) : (
                      <p className="text-muted mb-0">No task result available</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Close</Button>
          {comparisonData && !comparisonData.error && (
            <Button variant="primary">Export Comparison</Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>⚠️ Confirm Set Default Task Result</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-warning">
            <h6 className="alert-heading">Are you sure you want to proceed?</h6>
            <p className="mb-2">
              You are about to set the <strong>{confirmAction === 'original' ? 'original task attempt' : 'retake task attempt'}</strong> 
              as the default result for:
            </p>
            <ul className="mb-2">
              <li><strong>Student:</strong> {getStudentDisplayName()}</li>
              <li><strong>Assessment:</strong> {selectedScreeningPoint}</li>
              <li><strong>Task:</strong> {selectedTask}</li>
              <li><strong>Class:</strong> {selectedClassName}</li>
            </ul>
            <p className="mb-0">
              <strong>This action will:</strong>
            </p>
            <ul className="mb-2">
              <li>
                {confirmAction === 'original' 
                  ? 'Restore the original task attempt result as the active/default result'
                  : 'Keep the retake task attempt result as the active/default result'
                }
              </li>
              <li>Update the student's official assessment record for this specific task</li>
              <li>Affect how this task result appears in reports and dashboards</li>
              <li>Only change the result for this individual task, not the entire assessment</li>
            </ul>
            <hr />
            <p className="mb-0 text-muted">
              <em>Note: This change will be reflected immediately in the student's assessment history and cannot be easily undone. Other tasks in this assessment will remain unchanged.</em>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowConfirmModal(false)}
            disabled={isProcessingDefault}
          >
            Cancel
          </Button>
          <Button 
            variant={confirmAction === 'original' ? 'primary' : 'success'}
            onClick={confirmSetDefault}
            disabled={isProcessingDefault}
          >
            {isProcessingDefault ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Processing...
              </>
            ) : (
              <>
                ✓ Yes, Set {confirmAction === 'original' ? 'Original' : 'Retake'} as Default
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RetakeTaskComparisonModal;